import React from "react";
import { inject, observer } from "mobx-react";
import { observable, computed } from "mobx";
import { RouteComponentProps } from "react-router";

import { Formik, Field, FormikProps, FieldProps } from "formik";
import { TextField } from "@material-ui/core";
import {
  STForm,
  STButton,
  STProvidersGridRoot,
  STProvidersGridTitle,
  STProviderName,
  STProviderImg,
  STProvidersGridContent,
  STProvidersGrid,
} from "./styles";
import { IAuthStore, ISignInResult } from "Stores/Auth/types";
import { IValuesStore } from "Stores/Values/types";
import PostBackFormSubmittion from "Tools/PostBackFormSubmittion";
import { Values } from "Tools/Values";
import { required } from "Tools/FormValidations";
import { IManagementStore } from "@src/Stores/Management/types";
import { RouterStore } from "mobx-react-router";
import { STSelectLocation } from "../SelectLocation/styles";
import { AuthProvider } from "@src/Interfaces/Entity/OAuthProvider";
import { oauthLogInRoute, oauthLogInErrorRoute } from "@src/Stores/Auth/types";
import qs from "query-string";
import * as Yup from "yup";
import { IAuthByProviderRequestOptions, IAuthRequestOptions } from "../../../../Interfaces/AuthRequest";
import { customEncodeURI } from "../../../../Tools/Helpers/uriEncodingHelper";

const validationSchema = (hasAnyOAuthProviders: boolean) =>
  Yup.object().shape({
    login: Yup.string().test(
      "loginValidation",
      hasAnyOAuthProviders
        ? `${Values.getLocalizationValue("provideUserName")} ${Values.getLocalizationValue("orLoginViaProvider")}`
        : Values.getLocalizationValue("provideUserName"),
      function (value) {
        if (/^([\w\.\-]+)@([\w\-]+)((\.[\w\-]+)+)$/g.test(value)) {
          return false;
        }
        return true;
      }
    ),
  });

interface IProps extends RouteComponentProps {
  auth?: IAuthStore;
  values?: IValuesStore;
  management?: IManagementStore;
  routing?: RouterStore;
  forceUpdate?: any;
}
export interface IFormFields {
  login: string;
  password: string;
  location: string;
}

interface IOutputParams {
  ssopostback?: string;
  mode?: string;
  returnUrl?: string;
}

@inject("auth", "values", "routing", "management")
@observer
class LogIn extends React.Component<IProps> {
  @observable lastLocation: string = "";

  constructor(props: IProps) {
    super(props);
    const lastLocation: string = localStorage.getItem("lastLocation");
    if (lastLocation) {
      this.lastLocation = lastLocation;
    }
  }

  @computed get location() {
    let location = this.props.values.location ? this.props.values.location : this.lastLocation;
    if (!location && this.props.values.loginLocations.length === 1) {
      return this.props.values.loginLocations[0].id;
    }
  }

  @computed get hasAnyOAuthProviders() {
    return this.props.management.shortAuthProviders.length > 0;
  }
  handleSubmit = async (values: IFormFields) => {
    try {
      let returnUrl: string = null;
      //OAuth case
      if (
        (this.props.location.pathname.match(oauthLogInRoute) ||
          this.props.location.pathname.match(oauthLogInErrorRoute)) &&
        this.props.location.search.match("returnUrl")
      ) {
        returnUrl = new URLSearchParams(this.props.location.search).get("returnUrl");
      }

      const tenantParam = new URLSearchParams(this.props.location.search).get("tenant");
      const response: ISignInResult = await this.props.auth.signIn(
        values.login,
        values.password,
        values.location,
        tenantParam,
        returnUrl
      );
      if (response.isAuthenticated) {
        this.props.auth.redirectAfterLogin = response.redirectAfterLogin;
        if (
          this.props.auth.isTwoFactorAuthenticationRequired &&
          this.props.auth.isTwoFactorAuthenticationEnabledForUser
        ) {
            returnUrl = new URLSearchParams(this.props.location.search).get("returnUrl") ?? "/";
            let encodedUrl = customEncodeURI(returnUrl);
            this.props.routing.history.push("/login/2fa" + "?returnUrl=" + encodedUrl);
        } else {
            returnUrl = new URLSearchParams(this.props.location.search).get("returnUrl") ?? "/";
            window.location.href = response.redirectAfterLogin ? response.redirectAfterLogin : returnUrl;
        }
      } else {
        sessionStorage.setItem("InvalidCredentials", values.login);
        this.props.forceUpdate();
      }
    } catch (error) {
      console.error(error);
    }

    if (values.location) {
      localStorage.setItem("lastLocation", values.location);
    }
    };

  handleLoginViaExternalProvider = async (provider: AuthProvider, locationId: string) => {
    try {
        const queryString: IOutputParams = (qs.parse(this.props.location.search) as unknown) as IOutputParams;
        const returnUrl = new URLSearchParams(this.props.location.search).get("returnUrl");

        const queryLowerCased: IOutputParams = {};
        Object.entries(queryString).map(([key, value]) => {
        return (queryLowerCased[key.toLowerCase()] = value);
        });
        const tenantParam = new URLSearchParams(this.props.location.search).get("tenant");
        if ((this.props.location.pathname.match(oauthLogInRoute) || this.props.location.pathname.match(oauthLogInErrorRoute)) && this.props.location.search.match("response_type=code")) {
            let options: IAuthByProviderRequestOptions = {

                oAuthProviderId: provider.id,
                locationId: locationId,
                ssoPostBack: queryLowerCased.ssopostback || "",
                ssoPostBackMode: queryLowerCased.mode || "",
                ssoPostBackHash: this.props.location.hash.replace(
                    "#",
                    ""
                ) || "",
                associatedPersonId: null,
                afterAuthRedirectUrl: returnUrl
            }
            await this.props.auth.signInViaOAuth2ByProvider(this.props.location.search, options);
        } else {
            await this.props.auth.signInViaExternalProvider(
                provider.id,
                locationId,
                queryLowerCased.ssopostback || "",
                queryLowerCased.mode || "",
                this.props.location.hash || "",
                null,
                tenantParam,
                returnUrl
            );
        }
    } catch (error) {
      console.error(error);
    }
  };

  render() {
    const currentLocation = this.props.values.loginLocations.find(
      (location) => location.id === this.props.values.location
    );
    const defaultProviderImageurl = "/images/icon-oauth-default.png";
    const login =
      sessionStorage.getItem("InvalidCredentials") || new URLSearchParams(this.props.location.search).get("user") || "";
    return (
      <>
        <Formik
          onSubmit={this.handleSubmit}
          initialValues={{
            login: login,
            password: "",
            location: this.location,
          }}
          validationSchema={() => validationSchema(this.hasAnyOAuthProviders)}
          render={(formikProps: FormikProps<IFormFields>) => (
            <STForm onSubmit={formikProps.handleSubmit} data-test-id={"login-form"}>
              {!this.props.values.location && (
                <STSelectLocation value={formikProps.values.location} formikProps={formikProps} />
              )}
              <Field
                name="login"
                validate={required}
                render={({ field }: FieldProps) => (
                  <TextField
                    {...field}
                    fullWidth
                    data-test-id={"username-field"}
                    label={Values.getLocalizationValue("username")}
                    error={formikProps.touched.login && !!formikProps.errors.login}
                    helperText={formikProps.touched.login && formikProps.errors.login}
                    inputProps={{
                      autoCapitalize: "off",
                      autoCorrect: "off",
                    }}
                  />
                )}
              />
              <Field
                name="password"
                validate={required}
                render={({ field }: FieldProps) => (
                  <TextField
                    {...field}
                    fullWidth
                    data-test-id={"password-field"}
                    autoComplete="off"
                    label={Values.getLocalizationValue("password")}
                    type="password"
                    error={formikProps.touched.password && !!formikProps.errors.password}
                    helperText={formikProps.touched.password && formikProps.errors.password}
                  />
                )}
              />

              <STButton type="submit" variant="contained" color="primary">
                {Values.getLocalizationValue("signIn")}
              </STButton>

              {((this.props.values.location && currentLocation && currentLocation.primary) ||
                !this.props.values.location) &&
                this.hasAnyOAuthProviders && (
                  <STProvidersGridRoot>
                    <STProvidersGridTitle>{Values.getLocalizationValue("orSignInWith")}</STProvidersGridTitle>
                    <STProvidersGrid container>
                      {this.props.management.shortAuthProviders.map((provider: AuthProvider) => (
                        <STProvidersGridContent
                          key={provider.id}
                          onClick={() => this.handleLoginViaExternalProvider(provider, formikProps.values.location)}
                        >
                          <STProviderImg src={provider.iconUrl} onError={(e: any) => (e.target.onerror = null, e.target.src = defaultProviderImageurl)} />
                          <STProviderName>{provider.displayTitle}</STProviderName>
                        </STProvidersGridContent>
                      ))}
                    </STProvidersGrid>
                  </STProvidersGridRoot>
                )}
            </STForm>
          )}
        />
      </>
    );
  }
}

export default LogIn;
